import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../api/api";
import {
  LEGAL_DOCUMENTS,
  LEGAL_DOCUMENT_VERSIONS,
} from "../content/legalDocuments";
import authService from "../services/authService";

const LegalDocumentBody = ({ document }) => (
  <>
    <p className="text-muted small mb-3">
      Last updated {document.lastUpdated} · Version {document.version}
    </p>

    {document.introduction.map((paragraph) => (
      <p key={paragraph}>{paragraph}</p>
    ))}

    {document.sections.map((section) => (
      <section key={section.heading} className="mb-4">
        <h3 className="h6">{section.heading}</h3>

        {section.paragraphs?.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}

        {section.bullets && (
          <ul>
            {section.bullets.map((item) => (
              <li key={item} className="mb-1">
                {item}
              </li>
            ))}
          </ul>
        )}
      </section>
    ))}
  </>
);

const DocumentLink = ({ documentKey, onOpen }) => (
  <Button
    type="button"
    variant="link"
    className="p-0 align-baseline"
    onClick={() => onOpen(documentKey)}
  >
    {LEGAL_DOCUMENTS[documentKey].title}
  </Button>
);

const Terms = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [coreAgreed, setCoreAgreed] = useState(false);
  const [safetyAgreed, setSafetyAgreed] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const isClient = authService.isClient();
  const isTherapistLike = !isClient;
  const viewOnly = searchParams.get("view") === "1";

  const visibleDocuments = useMemo(
    () =>
      isClient
        ? ["privacy", "terms", "activitySafety"]
        : ["privacy", "terms"],
    [isClient]
  );

  const openDocument = (key) => setSelectedDocument(key);
  const closeDocument = () => setSelectedDocument(null);

  const canContinue = coreAgreed && (!isClient || safetyAgreed);

  const handleSubmit = async () => {
    if (!canContinue) return;

    setLoading(true);
    setError("");

    const agreements = {
      privacy: LEGAL_DOCUMENT_VERSIONS.privacy,
      terms: LEGAL_DOCUMENT_VERSIONS.terms,
    };

    if (isClient) {
      agreements.activity_safety = LEGAL_DOCUMENT_VERSIONS.activitySafety;
    }

    const acceptance = { type: "self" };

    try {
      await api.post("/terms/", { agreements, acceptance });
      navigate(authService.isClinicAdmin() && (!authService.isTherapist() || authService.isManagementOnly()) ? "/team" : isTherapistLike ? "/clients" : "/programs");
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          "We couldn't save your agreement. Please try again."
      );
      console.error("Terms submission error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (viewOnly) {
    return (
      <Container id="terms" className="py-3 py-md-4" style={{ maxWidth: "760px" }}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-3 p-md-5">
            <div className="text-center mb-4">
              <h1 className="h3 mb-2">Legal & privacy</h1>
              <p className="text-muted mb-0">
                Review the documents that apply to your Plasticise account.
              </p>
            </div>

            <div className="d-grid gap-3">
              {visibleDocuments.map((key) => {
                const document = LEGAL_DOCUMENTS[key];

                return (
                  <Card key={key} className="border">
                    <Card.Body className="p-3">
                      <h2 className="h6 mb-1">{document.title}</h2>
                      <p className="text-muted small mb-2">{document.summary}</p>
                      <Button
                        type="button"
                        variant="link"
                        className="p-0"
                        onClick={() => openDocument(key)}
                      >
                        Read full document
                      </Button>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          </Card.Body>
        </Card>

        <Modal
          show={Boolean(selectedDocument)}
          onHide={closeDocument}
          size="lg"
          scrollable
          centered
        >
          {selectedDocument && (
            <>
              <Modal.Header closeButton>
                <Modal.Title className="h5">
                  {LEGAL_DOCUMENTS[selectedDocument].title}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <LegalDocumentBody
                  document={LEGAL_DOCUMENTS[selectedDocument]}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button type="button" variant="primary" onClick={closeDocument}>
                  Close
                </Button>
              </Modal.Footer>
            </>
          )}
        </Modal>
      </Container>
    );
  }

  return (
    <Container id="terms" className="py-3 py-md-4" style={{ maxWidth: "680px" }}>
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-3 p-md-5">
          <div className="text-center mb-4">
            <h1 className="h3 mb-2">Welcome to Plasticise</h1>
            <p className="text-dark mb-1">
              {isClient
                ? "Before you start your program, please review and agree to the following."
                : "Just one quick step before you get started."}
            </p>
            <p className="text-muted small mb-0">
              You can open the full documents at any time.
            </p>
          </div>

          <Card className="border mb-3">
            <Card.Body className="p-3">
              <h2 className="h6 mb-2">Privacy & Terms</h2>
              <p className="text-muted small mb-2">
                Review how Plasticise handles information and the rules for using
                the platform.
              </p>
              <div className="d-flex flex-wrap gap-2 align-items-center small">
                <DocumentLink documentKey="privacy" onOpen={openDocument} />
                <span className="text-muted">·</span>
                <DocumentLink documentKey="terms" onOpen={openDocument} />
              </div>
            </Card.Body>
          </Card>

          {isClient && (
            <Card className="border mb-3">
              <Card.Body className="p-3">
                <h2 className="h6 mb-2">Using your program safely</h2>
                <p className="text-muted small mb-2">
                  Your therapist has designed your program for you. Stop an
                  activity if it causes significant pain, feels unsafe or causes
                  concerning symptoms, and contact your therapist if you are
                  unsure.
                </p>
                <DocumentLink
                  documentKey="activitySafety"
                  onOpen={openDocument}
                />
              </Card.Body>
            </Card>
          )}

          <div className="bg-light border rounded p-3 mb-3">
            <Form.Check
              id="agree-privacy-terms"
              type="checkbox"
              checked={coreAgreed}
              onChange={(event) => setCoreAgreed(event.target.checked)}
              label={
                isClient
                    ? "I agree to the Terms of Use and consent to Plasticise collecting and handling my personal and health information as described in the Privacy Policy."
                    : "I agree to the Terms of Use and acknowledge the Privacy Policy."
              }
            />

            {isClient && (
              <Form.Check
                id="agree-activity-safety"
                type="checkbox"
                className="mt-3"
                checked={safetyAgreed}
                onChange={(event) => setSafetyAgreed(event.target.checked)}
                label="I have read and understand the Activity Safety Acknowledgement."
              />
            )}
          </div>

          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Button
            type="button"
            variant="primary"
            className="w-100 py-3 custom-pink-button"
            disabled={!canContinue || loading}
            onClick={handleSubmit}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : isClient ? (
              "Agree & start my program"
            ) : (
              "Agree & continue"
            )}
          </Button>
        </Card.Body>
      </Card>

      <Modal
        show={Boolean(selectedDocument)}
        onHide={closeDocument}
        size="lg"
        scrollable
        centered
      >
        {selectedDocument && (
          <>
            <Modal.Header closeButton>
              <Modal.Title className="h5">
                {LEGAL_DOCUMENTS[selectedDocument].title}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <LegalDocumentBody document={LEGAL_DOCUMENTS[selectedDocument]} />
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="primary" onClick={closeDocument}>
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Container>
  );
};

export default Terms;
